/**
 * Commands that exist on every page: navigation and app-level actions. Page-
 * specific commands are registered by the page itself (e.g. the trip route).
 */
import { forceReloadApp } from '@/lib/pwa';
import { type PaletteCommand, useRegisterCommands } from '@/stores/commands';
import { useNavigate } from '@tanstack/react-router';
import { Home, ListChecks, Plane, RotateCw, Settings, Shield, Sparkles } from 'lucide-react';
import { useMemo } from 'react';

export function useGlobalCommands(isAdmin: boolean): void {
  const navigate = useNavigate();
  const commands = useMemo<PaletteCommand[]>(() => {
    const go = (to: string) => () => void navigate({ to });
    const nav: PaletteCommand[] = [
      { id: 'nav:home', label: 'Go to Home', group: 'Go to', icon: Home, run: go('/') },
      {
        id: 'nav:library',
        label: 'Go to Library',
        group: 'Go to',
        icon: ListChecks,
        keywords: ['master lists'],
        run: go('/library'),
      },
      {
        id: 'nav:activities',
        label: 'Go to Activities',
        group: 'Go to',
        icon: Sparkles,
        run: go('/activities'),
      },
      { id: 'nav:trips', label: 'Go to Trips', group: 'Go to', icon: Plane, run: go('/trips') },
      {
        id: 'nav:settings',
        label: 'Go to Settings',
        group: 'Go to',
        icon: Settings,
        keywords: ['text size', 'ai model'],
        run: go('/settings'),
      },
      ...(isAdmin
        ? [
            {
              id: 'nav:admin',
              label: 'Go to Admin',
              group: 'Go to',
              icon: Shield,
              run: go('/admin'),
            } satisfies PaletteCommand,
          ]
        : []),
    ];
    const app: PaletteCommand[] = [
      {
        id: 'app:refresh',
        label: 'Refresh the app',
        group: 'App',
        icon: RotateCw,
        keywords: ['reload', 'update', 'latest version'],
        run: () => void forceReloadApp(),
      },
    ];
    return [...nav, ...app];
  }, [navigate, isAdmin]);
  useRegisterCommands('global', commands);
}
